import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, PageLoader, CopyButton } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Network, Plus, QrCode } from "lucide-react";

export default function WireGuard() {
  const [servers, setServers] = useState(null);
  const [sid, setSid] = useState("");
  const [tunnels, setTunnels] = useState([]);
  const [form, setForm] = useState({ site_name: "", tunnel_name: "" });

  useEffect(() => {
    api.get("/servers").then((r) => {
      const ready = r.data.filter((s) => s.status === "healthy");
      setServers(ready);
      if (ready[0]) setSid(ready[0].id);
    });
  }, []);
  const load = (id) => api.get(`/wireguard/${id}`).then((r) => setTunnels(r.data));
  useEffect(() => { if (sid) load(sid); }, [sid]);
  if (!servers) return <PageLoader />;

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/wireguard", { server_id: sid, ...form });
      setForm({ site_name: "", tunnel_name: "" });
      toast.success("Tunnel created");
      load(sid);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="mb-2 flex items-center gap-2"><Network className="h-5 w-5 text-cyan-400" /><h1 className="font-display text-2xl font-bold text-slate-50">WireGuard Tunnels</h1></div>
      <p className="mb-6 text-sm text-slate-400">Create private encrypted tunnels between your Proxmox host and your backup server. Only backup-management services are exposed.</p>

      {servers.length === 0 ? (
        <GlassCard className="p-8 text-center text-slate-400">No ready servers yet.</GlassCard>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          <GlassCard className="p-6">
            <h2 className="mb-4 font-display font-semibold text-slate-100">New tunnel</h2>
            <form onSubmit={create} className="space-y-3">
              <select value={sid} onChange={(e) => setSid(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
                {servers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <Input required placeholder="Site / Node name" value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} data-testid="wg-site" className="bg-slate-950 border-slate-700" />
              <Input required placeholder="Tunnel name" value={form.tunnel_name} onChange={(e) => setForm({ ...form, tunnel_name: e.target.value })} data-testid="wg-tunnel" className="bg-slate-950 border-slate-700" />
              <button data-testid="wg-create" className="inline-flex items-center gap-2 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D] hover:bg-cyan-300"><Plus className="h-4 w-4" /> Generate configuration</button>
            </form>
          </GlassCard>

          <div className="space-y-3">
            {tunnels.length === 0 ? <GlassCard className="p-6 text-sm text-slate-500">No tunnels yet.</GlassCard> : tunnels.map((t) => (
              <GlassCard key={t.id} className="p-5" data-testid={`wg-tunnel-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div><div className="font-semibold text-slate-100">{t.tunnel_name}</div><div className="text-xs text-slate-500">{t.site_name} · {t.client_ip}</div></div>
                  <QrCode className="h-8 w-8 text-slate-600" />
                </div>
                <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950 p-3">
                  <div className="mb-1 flex justify-end"><CopyButton value={t.config} label="Config" testid={`wg-copy-${t.id}`} /></div>
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap font-mono text-xs text-cyan-300">{t.config}</pre>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
